import { Dictionary } from 'lodash';
import { Moment } from 'moment';
import React from 'react';
import { CostRate } from 'api/cost-rates';
import {
  DateType,
  Role as RoleT,
  Role,
  Team as TeamT,
  Team,
  User as UserT,
} from 'api/models';
import { ProjectPhase, ProjectForRm } from 'api/project';
import {
  AllocationCreatePayload,
  AllocationDeletePayload,
  AllocationMedium,
  AllocationUpdatePayload,
  CompanyResourceSummary,
  GenericResourceSummary,
  ResourceType,
  TeamLevelAggregation,
  TeamResourceSummary,
  UserLevelAggregation,
  UserResourceSummary,
} from 'api/resource-management/types';
import type { GroupedFilter } from 'api/tasks/filter-types';
import { Extra } from 'components/molecules/squad-picker';
import { DateRange } from '../projects-report/common/range-picker/types';
import { Minutes, WorkingDaysConfiguration } from '../user-capacity/types';
import { ResourceFlow } from './constants';
import { ViewPresetId } from './scheduler-config/view-presets/constants';

export enum GroupBy {
  Users = 'users',
  Projects = 'projects',
}

export enum View {
  Apps = 'apps',
  Demand = 'capacity-planning',
}
export interface AvailabilityFilter {
  startDate: Moment;
  endDate: Moment;
  availableMinutes: number;
  roles: number[];
  users: number[];
}
export interface ResourceManagementFilter {
  filter: GroupedFilter;
  availabilityFilter?: AvailabilityFilter;
}
export interface ResourceManagementDemandFilter {
  filter: GroupedFilter;
}
export interface ResourceManagementSearchParams
  extends ResourceManagementFilter {
  viewId?: number;
}

export interface ResourceManagementDemandSearchParams {
  filter: GroupedFilter;
  range: DateRange;
}
export interface ResourceManagementHashParams {
  viewPreset: ViewPresetId;
}
export type ResourceManagmentPageParams = ResourceManagementSearchParams &
  ResourceManagementHashParams;

export type ResourceManagementDemandPageParams =
  ResourceManagementDemandSearchParams & ResourceManagementHashParams;

export interface ExtendedUserIdentifier {
  type: ResourceType.User;
  user: UserT;
  role?: RoleT;
  referenceTeam?: TeamT;
}

export interface ExtendedTeamIdentifier {
  type: ResourceType.Team;
  team: TeamT;
}

export type ExtendedResourceIdentifier =
  | ExtendedUserIdentifier
  | ExtendedTeamIdentifier;

export interface AddResourceOptions {
  highlight?: boolean;
  expand?: boolean;
}
export interface AddResourceProps {
  projectId: number;
  resource: ExtendedResourceIdentifier;
  options?: AddResourceOptions;
  budgetIds?: number[];
}

export interface ProjectUser {
  user: UserT;
  role?: Role;
  referenceTeam?: Team;
  budgetIds?: number[];
}

export interface ProjectsWithMembers extends ProjectForRm {
  projectMembers: ProjectUser[];
}

export interface ProjectWithTeam {
  project: ProjectForRm | ProjectsWithMembers;
  role?: Role;
  referenceTeam?: Team;
  budgetIds?: number[];
}

export interface UserWithProjects {
  projects: ProjectWithTeam[];
  user: UserT;
}

export type UsersGroupedByCompany = { [id: number]: number[] };
export type TeamsGroupedByCompany = { [id: number]: Team[] };

export interface UserAsResourceOptions {
  includeAssignProject: boolean;
  showBudgets?: boolean;
  includeAssignBudgets?: boolean;
  addLoadingIndicator?: boolean;
  getAssignedProjectIds?: (userId: number) => number[];
}

export type TeamWithProject = TeamT & ProjectForRm;

export type TeamLite = Pick<
  TeamWithProject,
  'teamId' | 'projectId' | 'teamName' | 'roleId'
> & { startDate?: DateType; endDate?: DateType; allocatedMinutes?: number };

export type TeamWithProjectGroupedByRoleLookup = Dictionary<TeamWithProject[]>;

export interface RoleWithUsersAndTeams extends RoleT {
  projectMembers: ProjectUser[];
  teams: TeamT[];
}

export interface ProjectWithUserGroupedByRole extends ProjectForRm {
  teamMembersByRoles: RoleWithUsersAndTeams[];
  teams?: Team[];
  projectMembers: ProjectUser[];
}
interface UserOption extends UserT {
  extra?: Extra & {
    teamId?: number;
    roleId?: number;
    isRole?: boolean;
    value?: string;
  };
  label?: string;
}

export interface ProjectWithUsers extends ProjectForRm {
  projectMembers: ProjectUser[];
  teams: TeamT[];
}

export interface ProjectWithUserGroupedByRoleOptions {
  includeAssignUserOrPlaceholder: (projectId: number) => boolean;
  showTeams: boolean;
  userLookup?: Dictionary<UserT>;
  selectUser?: UserOption;
  resourceRequestType?: string;
  flowType?: ResourceFlow;
  addLoadingIndicator?: boolean;
  showDataLoading?: boolean;
}

export interface UserAllocationSummary {
  userId: number;
  startDate: DateType;
  endDate: DateType;
  allocatedMinutes: AllocatedMinutes;
  capacityMinutes: Minutes;
  forViewPreset: ViewPresetId;
}

export interface TeamAllocationSummary {
  roleId: number;
  startDate: DateType;
  endDate: DateType;
  allocatedMinutes: AllocatedMinutes;
  forViewPreset: ViewPresetId;
}

export interface UserProjectAllocationSummary {
  userId?: number;
  teamId?: number;
  referenceTeamId?: number;
  projectId: number;
  startDate: DateType;
  endDate: DateType;
  allocatedMinutes: AllocatedMinutes;
  capacityMinutes?: Minutes;
  forViewPreset: ViewPresetId;
}

export interface BudgetAllocationSummary {
  budgetId: number;
  projectId: number;
  startDate: DateType;
  endDate: DateType;
  allocatedMinutes: AllocatedMinutes;
  capacityMinutes?: Minutes;
  forViewPreset: ViewPresetId;
}

export interface ResourceAllocationSummary {
  resourceId: string;
  startDate: DateType;
  endDate: DateType;
  allocatedMinutes: AllocatedMinutes;
  forViewPreset: ViewPresetId;
  totalNumberOfDays?: number;
  includeSaturday?: boolean;
  includeSunday?: boolean;
  overAllocationResponsesList?: Array<{
    overAllocations: Array<{
      startDate: DateType;
      endDate: DateType;
      overAllocatedMinutes: Minutes;
    }>;
  }>;
}

export interface ProjectPhaseWithStyles extends ProjectPhase {
  groupKey: DateType;
  rowIndex: number;
  height: number;
  top: number;
}

type CreatePayloadMap = Dictionary<AllocationCreatePayload>;
type UpdatePayloadMap = Dictionary<AllocationUpdatePayload>;
type DeletePayloadMap = Dictionary<AllocationDeletePayload>;

export interface BulkActionPayloadMap {
  create: CreatePayloadMap;
  update: UpdatePayloadMap;
  delete: DeletePayloadMap;
}

export type SchedulerReference = React.MutableRefObject<any>;
export type ExcludeNonWorkingDaysParam = {
  excludeNonWorkingDays: boolean;
  workingDaysConfig: WorkingDaysConfiguration;
};
export interface Range {
  fromDate: Moment;
  toDate: Moment;
}
export interface AggregationLookup {
  [x: string]: Dictionary<UserLevelAggregation | TeamLevelAggregation>;
}

export interface ReplacementUserForTeam extends UserT {
  fullfilledMinutes: number;
  isInProject: boolean;
  utilization?: number;
  margin?: number;
  cost: number;
  costRate?: CostRate;
  forecastedUtilization?: number;
  overAllocation?: number;
  diversifiedRevenue?: number;
  projectMember?: boolean;
  // Grid-specific fields
  id?: string | number;
  displayName?: string;
  roleId?: number;
  role?: Role;
  name?: string;
  isRoleMatched?: boolean;
  sourceType?: 'USER';
  referenceTeamId?: number;
}

export interface AddToProjectRecordType<T> {
  key: number;
  entity: T;
  isSelected: boolean;
}

export interface AllocatedMinutes {
  [AllocationMedium.Hard]: Minutes;
  [AllocationMedium.Soft]: Minutes;
}
export interface DetailedAllocatedMinutes {
  usersAllocatedMinutes: AllocatedMinutes;
  teamsAllocatedMinutes: AllocatedMinutes;
}

export interface DetailedAllocationData {
  totalAllocatedMinutes: Minutes;
  hardAllocatedMinutes: Minutes;
  softAllocatedMinutes: Minutes;
  allocatedPercentage: number;
  availableMinutes: Minutes;
}

export interface AllocationStartAndEndDate {
  minStartDate?: string | null;
  maxEndDate?: string | null;
}

// TODO: Remove RawSummary and related types
export interface RawSummary {
  [year: number]: {
    [month: number]: {
      [weekNumber: number]: {
        [day: number]: DetailedAllocatedMinutes;
      };
    };
  };
}
export interface RawSummaryOptions<T extends RawSummary> {
  fromDate?: Moment;
  toDate?: Moment;
  existingSummary?: T;
}

export type ViewportDateRange = {
  new: { startDate: DateType; endDate: DateType };
  old?: { startDate: DateType; endDate: DateType };
};

export type FilteredUserType = {
  native: UserWithProjects[];
  partner: UserWithProjects[];
  currentUser: UserWithProjects[];
};

export type SummaryApiUserId = string;
export type SummaryApiCompanyId = string;
export type SummaryApiRoleId = string;
export type PeopleViewChildrenSummaryId = string;
export type ProjectViewChildrenSummaryId = string;

export type BudgetUserBreakdownKey = string; // Format: PROJECT:projectId::BUDGET:budgetId
export type BudgetUserBreakdownValue = Record<string, GenericResourceSummary[]>; // Key is userId

export type ResourceSummariesData = {
  userSummaryMap: Record<SummaryApiUserId, UserResourceSummary[]>;
  roleSummaryMap: Record<SummaryApiRoleId, TeamResourceSummary[]>;
  companySummaryMap: Record<SummaryApiCompanyId, CompanyResourceSummary[]>;
  childrenBreakdown: Record<
    PeopleViewChildrenSummaryId | ProjectViewChildrenSummaryId,
    GenericResourceSummary[]
  >;
  budgetUserBreakdown: Record<BudgetUserBreakdownKey, BudgetUserBreakdownValue>;
};
